import type { BuyerDistribuicao } from '@/lib/api'

export type DistributionTone = 'neutral' | 'success' | 'warning' | 'danger'

export const DISTRIBUTION_URGENT_DAYS = 15

export const isDistributionFlagEnabled = (value: number | null | undefined) => value === 1

export const parseDistributionDate = (value: string | null | undefined) => {
  if (!value) return null
  const normalized = value.includes('T') ? value : `${value}T00:00:00`
  const parsed = new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

type ResolveDistributionStatusInput = {
  registrationEnabled: boolean
  distribuicao: BuyerDistribuicao | null
  referenceDate?: Date
}

export type DistributionStatusMode = 'unavailable' | 'active' | 'grace' | 'inactive'

export type ResolvedDistributionStatus = {
  registrationEnabled: boolean
  special: boolean
  hasData: boolean
  expected: number
  currentMonth: number
  average: number
  gap: number
  gapToMonth: number
  gapToAverage: number
  deadline: Date | null
  deadlineActive: boolean
  goalReached: boolean
  monthGoalReached: boolean
  averageGoalReached: boolean
  benefitActive: boolean
  masterActive: boolean
  daysRemaining: number | null
  deadlineExpired: boolean
  tone: DistributionTone
  urgentRisk: boolean
  mode: DistributionStatusMode
}

const DAY_IN_MS = 1000 * 60 * 60 * 24

const toStartOfDay = (value: Date) => {
  const normalized = new Date(value)
  normalized.setHours(0, 0, 0, 0)
  return normalized
}

export const resolveDistributionStatus = ({
  registrationEnabled,
  distribuicao,
  referenceDate = new Date(),
}: ResolveDistributionStatusInput): ResolvedDistributionStatus => {
  const special = isDistributionFlagEnabled(distribuicao?.tabela_especial)
  const expected = Math.max(0, distribuicao?.faturamento_mensal_esperado ?? 0)
  const currentMonth = Math.max(0, distribuicao?.faturamento_mes_atual ?? 0)
  const average = Math.max(0, distribuicao?.faturamento_medio_ultimos_3_meses ?? 0)
  const deadline = parseDistributionDate(distribuicao?.data_para_alcancar)
  const today = toStartOfDay(referenceDate)
  const deadlineActive = distribuicao?.prazo_ativo != null
    ? isDistributionFlagEnabled(distribuicao.prazo_ativo)
    : Boolean(deadline && deadline.getTime() >= today.getTime())
  const gapToMonth = Math.max(0, expected - currentMonth)
  const gapToAverage = Math.max(0, expected - average)
  const gap = Math.max(0, expected - Math.max(currentMonth, average))
  const monthGoalReached = distribuicao?.meta_alcancada_no_mes != null
    ? isDistributionFlagEnabled(distribuicao.meta_alcancada_no_mes)
    : expected > 0 && currentMonth >= expected
  const averageGoalReached = distribuicao?.meta_alcancada_media_3_meses != null
    ? isDistributionFlagEnabled(distribuicao.meta_alcancada_media_3_meses)
    : expected > 0 && average >= expected
  const goalReached = distribuicao?.meta_alcancada != null
    ? isDistributionFlagEnabled(distribuicao.meta_alcancada)
    : monthGoalReached || averageGoalReached
  const benefitActive = special
    ? registrationEnabled
    : distribuicao?.beneficio_ativo != null
      ? isDistributionFlagEnabled(distribuicao.beneficio_ativo)
      : registrationEnabled && (deadlineActive || goalReached)
  const masterActive = registrationEnabled && (special || benefitActive)
  const daysRemaining = deadline && deadlineActive
    ? Math.max(0, Math.ceil((deadline.getTime() - today.getTime()) / DAY_IN_MS))
    : null
  const deadlineExpired = deadline && !deadlineActive
    ? deadline.getTime() < today.getTime()
    : false
  const mode: DistributionStatusMode = !distribuicao
    ? 'unavailable'
    : special && masterActive
      ? 'active'
      : benefitActive && goalReached
      ? 'active'
      : benefitActive && deadlineActive
        ? 'grace'
        : 'inactive'
  const tone: DistributionTone = mode === 'unavailable'
    ? 'neutral'
    : mode === 'active'
      ? 'success'
      : mode === 'grace' && (daysRemaining !== null && daysRemaining <= DISTRIBUTION_URGENT_DAYS)
        ? 'danger'
        : mode === 'grace'
          ? 'warning'
          : 'danger'
  const urgentRisk = mode === 'grace' && tone === 'danger'

  return {
    registrationEnabled,
    special,
    hasData: Boolean(distribuicao),
    expected,
    currentMonth,
    average,
    gap,
    gapToMonth,
    gapToAverage,
    deadline,
    deadlineActive,
    goalReached,
    monthGoalReached,
    averageGoalReached,
    benefitActive,
    masterActive,
    daysRemaining,
    deadlineExpired,
    tone,
    urgentRisk,
    mode,
  }
}
